<template>
  <div>
    <h1>
      <v-icon color="black">mdi-image-outline</v-icon>
      {{ $t("chapter.image.imageSettings") }}
    </h1>
    <v-expansion-panels multiple>
      <v-expansion-panel>
        <v-expansion-panel-header>{{
          $t("chapter.image.imageContents")
        }}</v-expansion-panel-header>
        <v-expansion-panel-content v-if="editor">
          <div>
            <v-hover>
              <template v-slot:default="{ hover }">
                <v-card elevation="0" color="transparent mb-3">
                  <v-card-text>
                    <img
                      v-if="imageUrl"
                      class="image-preview"
                      :src="imageUrl"
                      :style="'border-radius: ' + radius + '%'"
                    />
                    <div
                      :class="
                        imageUrl
                          ? null
                          : 'py-15 mb-4 image-upload-container upload-container rounded-lg'
                      "
                    >
                      <v-fade-transition>
                        <v-overlay
                          v-show="
                            (hover && !loading) || !imageUrl || imageUrl == ''
                          "
                          absolute
                          :color="imageUrl ? 'black' : 'transparent'"
                        >
                          <MediaObjectUpload
                            v-model="uploadUrl"
                            :addText="$t('chapter.image.chooseImage')"
                            :changeText="$t('chapter.image.chooseImage')"
                            @error="setErrorMessage"
                            @loading="loading = $event"
                            :errorTooLargeMessage="
                              $t('register.imageTooLarge', {
                                maxImageSize: MAX_IMAGE_SIZE_MB,
                              })
                            "
                            :maxSizeBytes="MAX_IMAGE_SIZE_BYTES"
                            :formats="IMAGE_FORMATS"
                          />
                        </v-overlay>
                      </v-fade-transition>
                    </div>
                  </v-card-text>
                </v-card>
              </template>
            </v-hover>
          </div>
          <UploadErrorMessage :errorMessage="errorMessage" :loading="loading" />
          <div class="text-left my-3" v-if="loading">
            {{ $t("forms.uploading") }}
            <v-progress-linear
              indeterminate
              color="primaryAction darken-2"
            ></v-progress-linear>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>
          {{ $t("chapter.image.imageSettings") }}
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-container>
            <v-row>
            <v-col cols=6>
              <v-btn @click="setFitMode" x-small class="rounded-lg py-5" :color="fitModeActive ? 'secondaryAction' : 'background'" 
                ><span class="material-icons"> fit_screen </span>{{ $t('chapter.image.fitMode') }}</v-btn
              >
            </v-col>
            <v-col cols=6>
              <v-btn x-small :color="fullModeActive ? 'secondaryAction' : 'background'" class="rounded-lg py-5" @click="setFullMode"
                ><span class="material-icons"> fullscreen </span>{{ $t('chapter.image.fullMode') }}</v-btn
              >
            </v-col>

            </v-row>
            <v-row>
              <h3>
                {{ $t("chapter.image.cornerRadius", { radius: radius }) }}
              </h3>
            </v-row>
            <v-row>
              <v-slider
                min="0"
                max="50"
                color="primaryAction"
                @change="updateRadiusAttr"
                v-model="radius"
                track-color="primaryAction"
              ></v-slider>
            </v-row>
          </v-container></v-expansion-panel-content
        >
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>
<script>
import UploadErrorMessage from "./UploadErrorMessage.vue"
const EXTENSION_NAME = "image";
import MediaObjectUpload from "@/components/MediaObjectUpload";
import { IMAGE_FORMATS, MAX_IMAGE_UPLOAD_BYTES, MAX_IMAGE_UPLOAD_MEGABYTES } from "@/const";
import { AUTH_TOKEN } from "@/apollo/vue-apollo";
export default {
  props: {
    editor: Object,
  },
  components: {
    MediaObjectUpload,
    UploadErrorMessage,
  },
  data() {
    return {
      loading: false,
      uploadUrl: null,
      errorMessage: "",
      startRadius: this.editor.getAttributes(EXTENSION_NAME).radius,
      radius: 0,
    };
  },
  mounted() {
    this.radius = this.editor.getAttributes(EXTENSION_NAME).radius;
    if (this.editor) {
      this.editor.on("selectionUpdate", () =>
        this.handleEditorSelectionUpdate()
      );
    }
  },
  methods: {
    setErrorMessage(message) {
      this.errorMessage = message;
    },
    handleEditorSelectionUpdate() {
      if (this.isActive) {
        this.radius = this.editor.getAttributes(EXTENSION_NAME).radius;
      }
    },
    updateRadiusAttr(newValue) {
      this.editor.chain().focus().setRadius(newValue).run();
    },
    updateImageSrcAttr(event) {
      this.editor.chain().focus().setSrc(event).run();
    },
    setFullMode() {
      this.editor.chain().focus().setFullMode().run();
    },
    setFitMode() {
      this.editor.chain().focus().setFitMode().run();
    },
    align(alignment) {
      this.editor.chain().focus().setTextAlign(alignment).run();
    },
  },
  computed: {
    MAX_IMAGE_SIZE_MB() {
      return MAX_IMAGE_UPLOAD_MEGABYTES;
    },
    MAX_IMAGE_SIZE_BYTES() {
      return MAX_IMAGE_UPLOAD_BYTES;
    },
    IMAGE_FORMATS() {
      return IMAGE_FORMATS;
    },
    jwt() {
      return localStorage.getItem(AUTH_TOKEN);
    },
    isActive() {
      return this.editor.isActive(EXTENSION_NAME);
    },
    imageUrl() {
      return this.editor.getAttributes(EXTENSION_NAME).src;
    },
    fullModeActive() {
      return this.editor.getAttributes(EXTENSION_NAME).fullMode;
    },
    fitModeActive() {
      return this.editor.getAttributes(EXTENSION_NAME).fitMode;
    },
  },
  watch: {
    uploadUrl(newValue) {
      this.editor.chain().focus().setImageSrc(newValue).run();
    },
    imageUrl(newValue) {
      this.uploadUrl = newValue;
    },
  },
};
</script>
<style lang="scss" scoped>
.image-preview {
  height: auto;
  max-width: 100%;
  width: auto;
}
.image-upload-container {
  background-color: var(--v-background-base);
  width: 100%;
  border: 2px dashed #949191;
}
</style>